import { useEffect, useState } from 'react'
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { getMyTeamData as queryFn } from '../infrastructure/api-widget'
import { getInitialJitter } from '../helpers/resilience'
import { useToken } from './useToken'
import { safeSessionStorage } from '../helpers/sessionStorage'

type MyTeamData = Awaited<ReturnType<typeof queryFn>>

type UseMyTeamReturn = Omit<UseQueryResult<MyTeamData, Error>, 'data'> & {
    myTeam: MyTeamData | undefined
    isWaitingForJitter: boolean
}

/**
 * Hook para obtener datos del equipo del usuario con Thundering Herd Prevention
 * El jitter se calcula UNA VEZ por sesión y se respeta aunque recargue la página.
 *
 * @param props.enabled - Control adicional de habilitación (AND con el jitter interno)
 * @returns Query de TanStack con datos del equipo, isWaitingForJitter y estado de loading/error
 */
export const useMyTeam = (props?: { enabled?: boolean }): UseMyTeamReturn => {
    const MAX_JITTER_SECONDS = 60
    const { _profileId, _id } = useToken()

    /**
     * Lazy initialization del estado para evitar flash de UI
     * Calcula el estado inicial correctamente sin esperar al useEffect
     */
    const [isJitterComplete, setIsJitterComplete] = useState(() => {
        // Si no hay _id/_profileId aún (usuario sin autenticar), esperar
        if (!_id || !_profileId) return false

        const STORAGE_KEY = `myTeam_jitter_release_${_id}_${_profileId}`
        const storedRelease = safeSessionStorage.getItem(STORAGE_KEY)

        // Si no hay timestamp guardado, aún no completó el jitter
        if (!storedRelease) return false

        // Si hay timestamp, verificar si ya pasó el tiempo
        const releaseTimestamp = parseInt(storedRelease, 10)

        // Si el timestamp está corrupto, retornar false para que useEffect lo recalcule
        if (isNaN(releaseTimestamp)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[Thundering Herd Prevention] Corrupted timestamp in lazy init, will recalculate in useEffect', {
                    storedRelease,
                })
            }
            return false
        }

        const isComplete = Date.now() >= releaseTimestamp

        if (process.env.NODE_ENV === 'development' && isComplete) {
            console.info('[Thundering Herd Prevention] useMyTeam lazy init: jitter already completed', {
                releaseTimestamp: new Date(releaseTimestamp).toISOString(),
            })
        }

        return isComplete
    })

    // Aplicar Jitter Inicial con persistencia en sessionStorage (0-60s)
    useEffect(() => {
        // Early return: No ejecutar si faltan credenciales (usuario sin autenticar)
        // Esto previene crear storage keys inválidos como "myTeam_jitter_release_undefined_undefined"
        if (!_id || !_profileId) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[Thundering Herd Prevention] useMyTeam skipped: missing _id or _profileId (user not authenticated yet)')
            }
            return
        }

        const STORAGE_KEY = `myTeam_jitter_release_${_id}_${_profileId}`

        // Limpiar storage keys antiguas de otras sesiones/usuarios
        // Esto previene que se acumulen keys huérfanas en sessionStorage
        // Iteramos hacia atrás para evitar index shifting cuando removemos items
        const STORAGE_PREFIX = 'myTeam_jitter_release_'
        try {
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const key = sessionStorage.key(i)
                if (key?.startsWith(STORAGE_PREFIX) && key !== STORAGE_KEY) {
                    sessionStorage.removeItem(key)
                    if (process.env.NODE_ENV === 'development') {
                        console.info('[Thundering Herd Prevention] Cleaned up old storage key', { key })
                    }
                }
            }
        } catch (e) {
            // Falló la limpieza, no es crítico
            if (process.env.NODE_ENV === 'development') {
                console.warn('[Thundering Herd Prevention] Failed to clean up old storage keys', { error: e })
            }
        }

        const storedRelease = safeSessionStorage.getItem(STORAGE_KEY)
        const now = Date.now()

        if (storedRelease) {
            // Ya calculamos un jitter antes, respetarlo
            const releaseTimestamp = parseInt(storedRelease, 10)

            // Validar timestamp corrupto
            if (isNaN(releaseTimestamp)) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('[Thundering Herd Prevention] Corrupted timestamp detected, recalculating jitter', {
                        storedRelease,
                    })
                }

                // Limpiar valor corrupto
                safeSessionStorage.setItem(STORAGE_KEY, '')

                // Recalcular jitter como si fuera la primera vez
                const jitterDelay = getInitialJitter(MAX_JITTER_SECONDS)
                const newReleaseTimestamp = now + jitterDelay
                safeSessionStorage.setItem(STORAGE_KEY, newReleaseTimestamp.toString())

                if (process.env.NODE_ENV === 'development') {
                    console.info('[Thundering Herd Prevention] Recalculated jitter after corruption', {
                        delayMs: jitterDelay,
                        delaySeconds: Math.round(jitterDelay / 1000),
                        releaseTimestamp: new Date(newReleaseTimestamp).toISOString(),
                    })
                }

                const timer = setTimeout(() => {
                    setIsJitterComplete(true)
                }, jitterDelay)

                return () => clearTimeout(timer)
            }

            if (now >= releaseTimestamp) {
                // Ya pasó el tiempo, habilitar inmediatamente
                // (Normalmente ya estaría en true por lazy initialization, pero por si acaso)
                if (process.env.NODE_ENV === 'development') {
                    console.info('[Thundering Herd Prevention] useMyTeam jitter already completed (from previous mount)', {
                        releaseTimestamp: new Date(releaseTimestamp).toISOString(),
                        now: new Date(now).toISOString(),
                    })
                }
                setIsJitterComplete(true)
            } else {
                // Falta tiempo, esperar solo el remanente
                const remainingDelay = releaseTimestamp - now

                if (process.env.NODE_ENV === 'development') {
                    console.info('[Thundering Herd Prevention] useMyTeam resuming jitter from previous mount', {
                        remainingMs: remainingDelay,
                        remainingSeconds: Math.round(remainingDelay / 1000),
                        releaseTimestamp: new Date(releaseTimestamp).toISOString(),
                    })
                }

                const timer = setTimeout(() => {
                    setIsJitterComplete(true)
                }, remainingDelay)

                // Cleanup: Cancelar timer si el componente se desmonta antes del delay
                return () => clearTimeout(timer)
            }
        } else {
            // Primera vez, calcular jitter y guardar timestamp de liberación
            const jitterDelay = getInitialJitter(MAX_JITTER_SECONDS)
            const releaseTimestamp = now + jitterDelay
            const writeSuccess = safeSessionStorage.setItem(STORAGE_KEY, releaseTimestamp.toString())

            if (process.env.NODE_ENV === 'development') {
                console.info('[Thundering Herd Prevention] useMyTeam initial jitter calculated', {
                    delayMs: jitterDelay,
                    delaySeconds: Math.round(jitterDelay / 1000),
                    releaseTimestamp: new Date(releaseTimestamp).toISOString(),
                    storagePersisted: writeSuccess,
                })
            }

            const timer = setTimeout(() => {
                setIsJitterComplete(true)
            }, jitterDelay)

            // Cleanup: Cancelar timer si el componente se desmonta antes del delay
            return () => clearTimeout(timer)
        }
    }, [_id, _profileId])

    const { data: myTeam, ...rest } = useQuery({
        queryKey: ['User', _id, 'My Team', _profileId],
        queryFn,
        staleTime: 1000 * 60 * 5, // 5 minutes
        // enabled combina: jitter completado AND enabled externo (si existe)
        enabled: isJitterComplete && (props?.enabled ?? true),
    })

    return {
        myTeam,
        isWaitingForJitter: !isJitterComplete, // Exponer estado para UI contextual
        ...rest,
    }
}
